import type { UserRole } from '@/stores/auth-store';
import { canViewPage } from '@/lib/hrm-permissions';
import type { DashboardMenuItem } from '@/types/hrm-dashboard-config';

// Navigation page definition - matches hrm-app.js pages registry
export interface NavPage {
  key: string;
  title: string;
  icon: string;
  section: string;
  badge?: string;
}

export interface NavSection {
  key: string;
  label: string;
  order: number;
  pages: NavPage[];
}

/**
 * Sidebar IA (page keys unchanged — only grouping):
 * Main → Self Service → People → Organization → Time → Leave → Payroll
 * → Recruitment → Performance → Learning → Operations → Engagement
 * → Insights → Compliance → Administration
 *
 * Merges tiny sections (Assets / Projects / Surveys / Lifecycle / Management)
 * and separates daily employee tools from admin config.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'main', label: 'Main', order: 0,
    pages: [
      { key: 'dashboard', title: 'Dashboard', icon: 'ri-dashboard-3-line', section: 'main' },
    ],
  },
  {
    key: 'self', label: 'Self Service', order: 1,
    pages: [
      { key: 'employee-self-service', title: 'My Self-Service', icon: 'ri-user-settings-line', section: 'self' },
      { key: 'my-team', title: 'My Team', icon: 'ri-group-line', section: 'self' },
      { key: 'punch-clock', title: 'Punch Clock', icon: 'ri-timer-flash-line', section: 'self' },
      { key: 'attendance', title: 'Attendance', icon: 'ri-calendar-check-line', section: 'self' },
      { key: 'regularization', title: 'Regularization', icon: 'ri-edit-line', section: 'self' },
      { key: 'wfh-requests', title: 'WFH Requests', icon: 'ri-home-4-line', section: 'self' },
      { key: 'leave-balance', title: 'Leave Balance', icon: 'ri-pie-chart-line', section: 'self' },
      { key: 'leave-applications', title: 'Leave Applications', icon: 'ri-file-list-3-line', section: 'self' },
      { key: 'leave-team-calendar', title: 'Team Calendar', icon: 'ri-team-line', section: 'self' },
      { key: 'payslips', title: 'Payslips', icon: 'ri-file-text-line', section: 'self' },
      { key: 'timesheets', title: 'Timesheets', icon: 'ri-timer-line', section: 'self' },
      { key: 'notifications', title: 'Notifications', icon: 'ri-notification-3-line', section: 'self' },
      { key: 'quick-actions', title: 'Quick Actions', icon: 'ri-flashlight-line', section: 'self' },
      { key: 'help-center', title: 'Help Center', icon: 'ri-question-line', section: 'self' },
    ],
  },
  {
    key: 'people', label: 'People', order: 2,
    pages: [
      { key: 'employee-directory', title: 'Employee Directory', icon: 'ri-team-line', section: 'people' },
      { key: 'self-onboard-invites', title: 'Self-Registration Invites', icon: 'ri-mail-send-line', section: 'people' },
      { key: 'onboarding', title: 'Onboarding', icon: 'ri-user-follow-line', section: 'people' },
      { key: 'onboarding-guide', title: 'Onboarding guide', icon: 'ri-book-open-line', section: 'people' },
      { key: 'offboarding', title: 'Offboarding', icon: 'ri-user-unfollow-line', section: 'people' },
    ],
  },
  {
    key: 'org', label: 'Organization', order: 3,
    pages: [
      { key: 'org-structure', title: 'Org structure', icon: 'ri-organization-chart', section: 'org' },
      { key: 'legal-entities', title: 'Legal entities', icon: 'ri-bank-line', section: 'org' },
      { key: 'business-units', title: 'Business units', icon: 'ri-building-2-line', section: 'org' },
      { key: 'locations', title: 'Locations', icon: 'ri-map-pin-line', section: 'org' },
      { key: 'departments', title: 'Departments', icon: 'ri-building-4-line', section: 'org' },
      { key: 'cost-centers', title: 'Cost centers', icon: 'ri-price-tag-3-line', section: 'org' },
      { key: 'pay-grades', title: 'Pay grades', icon: 'ri-bar-chart-horizontal-line', section: 'org' },
      { key: 'pay-bands', title: 'Pay bands', icon: 'ri-stack-line', section: 'org' },
    ],
  },
  {
    key: 'time', label: 'Time & Attendance', order: 4,
    pages: [
      { key: 'attendance-summary', title: 'Attendance Summary', icon: 'ri-bar-chart-line', section: 'time' },
      { key: 'shifts', title: 'Shifts', icon: 'ri-time-line', section: 'time' },
      { key: 'shift-roster', title: 'Shift Roster', icon: 'ri-calendar-schedule-line', section: 'time' },
      { key: 'shift-swaps', title: 'Shift Swaps', icon: 'ri-repeat-line', section: 'time' },
      { key: 'overtime-management', title: 'Overtime', icon: 'ri-time-line', section: 'time' },
      { key: 'geofence', title: 'Geofence', icon: 'ri-map-pin-user-line', section: 'time' },
      { key: 'field-tracker', title: 'Field Tracker', icon: 'ri-radar-line', section: 'time' },
      { key: 'wfh-settings', title: 'WFH Policy', icon: 'ri-home-gear-line', section: 'time' },
    ],
  },
  {
    key: 'leave', label: 'Leave', order: 5,
    pages: [
      { key: 'leave-approvals', title: 'Leave Approvals', icon: 'ri-check-double-line', section: 'leave' },
      { key: 'leave-types', title: 'Leave Types', icon: 'ri-leaf-line', section: 'leave' },
      { key: 'leave-settings', title: 'Leave Settings', icon: 'ri-settings-4-line', section: 'leave' },
      { key: 'leave-encashment', title: 'Leave Encashment', icon: 'ri-money-dollar-circle-line', section: 'leave' },
      { key: 'holiday-calendar', title: 'Holidays', icon: 'ri-calendar-event-line', section: 'leave' },
    ],
  },
  {
    key: 'payroll', label: 'Payroll', order: 6,
    pages: [
      { key: 'payroll-dashboard', title: 'Payroll Dashboard', icon: 'ri-dashboard-line', section: 'payroll' },
      { key: 'salary-components', title: 'Salary Components', icon: 'ri-money-dollar-circle-line', section: 'payroll' },
      { key: 'salary-structures', title: 'Salary Structures', icon: 'ri-stack-line', section: 'payroll' },
      { key: 'payroll-run', title: 'Payroll Run', icon: 'ri-play-circle-line', section: 'payroll' },
      { key: 'loan-management', title: 'Loans & Advances', icon: 'ri-bank-line', section: 'payroll' },
    ],
  },
  {
    key: 'recruit', label: 'Recruitment', order: 7,
    pages: [
      { key: 'job-postings', title: 'Job Postings', icon: 'ri-search-eye-line', section: 'recruit' },
      { key: 'requisitions', title: 'Requisitions', icon: 'ri-file-add-line', section: 'recruit' },
      { key: 'candidate-pipeline', title: 'Candidates', icon: 'ri-user-add-line', section: 'recruit' },
      { key: 'resume-parser', title: 'Resume Parser', icon: 'ri-file-search-line', section: 'recruit' },
      { key: 'employee-referrals', title: 'Referrals', icon: 'ri-share-forward-line', section: 'recruit' },
      { key: 'interview-schedule', title: 'Interviews', icon: 'ri-video-chat-line', section: 'recruit' },
      { key: 'offer-management', title: 'Offers', icon: 'ri-mail-send-line', section: 'recruit' },
      { key: 'career-fair', title: 'Career Fair', icon: 'ri-presentation-line', section: 'recruit' },
      { key: 'hiring-analytics', title: 'Hiring Analytics', icon: 'ri-bar-chart-box-line', section: 'recruit' },
      { key: 'internal-mobility', title: 'Internal Mobility', icon: 'ri-exchange-line', section: 'recruit' },
    ],
  },
  {
    key: 'perf', label: 'Performance', order: 8,
    pages: [
      { key: 'performance-cycles', title: 'Cycles', icon: 'ri-trophy-line', section: 'perf' },
      { key: 'goals', title: 'Goals & OKRs', icon: 'ri-target-line', section: 'perf' },
      { key: 'feedback', title: '360° Feedback', icon: 'ri-chat-voice-line', section: 'perf' },
      { key: 'nine-box', title: 'Nine Box Grid', icon: 'ri-grid-line', section: 'perf' },
      { key: 'calibration', title: 'Calibration', icon: 'ri-scales-3-line', section: 'perf' },
      { key: 'competency-framework', title: 'Competencies', icon: 'ri-bar-chart-grouped-line', section: 'perf' },
      { key: 'pip-tracker', title: 'PIP Tracker', icon: 'ri-error-warning-line', section: 'perf' },
      { key: 'team-performance', title: 'Team Performance', icon: 'ri-team-fill', section: 'perf' },
    ],
  },
  {
    key: 'training', label: 'Learning', order: 9,
    pages: [
      { key: 'training', title: 'Training', icon: 'ri-book-open-line', section: 'training' },
      { key: 'training-calendar', title: 'Training Calendar', icon: 'ri-calendar-schedule-line', section: 'training' },
      { key: 'training-materials', title: 'Materials', icon: 'ri-folder-video-line', section: 'training' },
      { key: 'training-certificates', title: 'Certificates', icon: 'ri-award-line', section: 'training' },
      { key: 'training-feedback', title: 'Training Feedback', icon: 'ri-chat-smile-2-line', section: 'training' },
      { key: 'learning-paths', title: 'Learning Paths', icon: 'ri-road-map-line', section: 'training' },
      { key: 'mentoring-program', title: 'Mentoring', icon: 'ri-user-heart-line', section: 'training' },
    ],
  },
  {
    key: 'ops', label: 'Operations', order: 10,
    pages: [
      { key: 'expenses', title: 'Expenses', icon: 'ri-receipt-line', section: 'ops' },
      { key: 'travel-management', title: 'Travel', icon: 'ri-flight-takeoff-line', section: 'ops' },
      { key: 'tickets', title: 'Support Tickets', icon: 'ri-customer-service-2-line', section: 'ops' },
      { key: 'projects', title: 'Projects', icon: 'ri-kanban-view-2', section: 'ops' },
    ],
  },
  {
    key: 'assetmod', label: 'Assets', order: 10.5,
    pages: [
      { key: 'assets', title: 'Inventory', icon: 'ri-computer-line', section: 'assetmod' },
      { key: 'asset-categories', title: 'Categories', icon: 'ri-folders-line', section: 'assetmod' },
      { key: 'asset-requests', title: 'Requests', icon: 'ri-inbox-line', section: 'assetmod' },
      { key: 'asset-maintenance', title: 'Maintenance', icon: 'ri-tools-line', section: 'assetmod' },
      { key: 'asset-cover', title: 'Cover', icon: 'ri-shield-check-line', section: 'assetmod' },
      { key: 'asset-incidents', title: 'Incidents', icon: 'ri-alarm-warning-line', section: 'assetmod' },
      { key: 'asset-labels', title: 'Labels', icon: 'ri-qr-code-line', section: 'assetmod' },
      { key: 'asset-controls', title: 'Controls', icon: 'ri-settings-3-line', section: 'assetmod' },
    ],
  },
  {
    key: 'engage', label: 'Engagement', order: 11,
    pages: [
      { key: 'announcements', title: 'Announcements', icon: 'ri-megaphone-line', section: 'engage' },
      { key: 'policies', title: 'Policies', icon: 'ri-shield-check-line', section: 'engage' },
      { key: 'benefits', title: 'Benefits', icon: 'ri-gift-line', section: 'engage' },
      { key: 'rewards', title: 'Rewards', icon: 'ri-vip-crown-line', section: 'engage' },
      { key: 'documents', title: 'Documents', icon: 'ri-folder-line', section: 'engage' },
      { key: 'company-calendar', title: 'Company Calendar', icon: 'ri-calendar-event-fill-line', section: 'engage' },
      { key: 'recognition-wall', title: 'Recognition Wall', icon: 'ri-heart-3-line', section: 'engage' },
      { key: 'meeting-scheduler', title: 'Meeting Scheduler', icon: 'ri-calendar-line', section: 'engage' },
      { key: 'grievance-management', title: 'Grievances', icon: 'ri-chat-delete-line', section: 'engage' },
      { key: 'employee-engagement', title: 'Engagement Score', icon: 'ri-heart-pulse-fill-line', section: 'engage' },
      { key: 'surveys', title: 'Surveys', icon: 'ri-survey-line', section: 'engage' },
      { key: 'pulse-survey', title: 'Pulse Survey', icon: 'ri-heart-pulse-line', section: 'engage' },
      { key: 'survey-analytics', title: 'Survey Analytics', icon: 'ri-pie-chart-2-line', section: 'engage' },
    ],
  },
  {
    key: 'analytics', label: 'Insights', order: 12,
    pages: [
      { key: 'reports', title: 'Reports', icon: 'ri-bar-chart-box-line', section: 'analytics' },
      { key: 'workforce-planning', title: 'Workforce Planning', icon: 'ri-team-fill', section: 'analytics' },
      { key: 'attrition-risk', title: 'Attrition Risk', icon: 'ri-alarm-warning-line', section: 'analytics' },
      { key: 'separation-analysis', title: 'Separation Analysis', icon: 'ri-user-unfollow-fill-line', section: 'analytics' },
    ],
  },
  {
    key: 'compliance', label: 'Compliance', order: 13,
    pages: [
      { key: 'compliance-dashboard', title: 'Compliance', icon: 'ri-shield-star-line', section: 'compliance' },
      { key: 'contingent-workers', title: 'Contractors', icon: 'ri-user-unfollow-line', section: 'compliance' },
      { key: 'visa-immigration', title: 'Visa & Immigration', icon: 'ri-passport-line', section: 'compliance' },
      { key: 'workplace-safety', title: 'Workplace Safety', icon: 'ri-first-aid-kit-line', section: 'compliance' },
      { key: 'risk-register', title: 'Risk Register', icon: 'ri-alarm-warning-line', section: 'compliance' },
    ],
  },
  {
    key: 'system', label: 'Administration', order: 14,
    pages: [
      { key: 'setup', title: 'Workspace setup', icon: 'ri-rocket-line', section: 'system' },
      { key: 'settings', title: 'Settings', icon: 'ri-settings-3-line', section: 'system' },
      { key: 'picklists', title: 'Picklists', icon: 'ri-list-settings-line', section: 'system' },
      { key: 'roles-permissions', title: 'Roles & Permissions', icon: 'ri-lock-line', section: 'system' },
      { key: 'audit-log', title: 'Audit Log', icon: 'ri-file-list-3-line', section: 'system' },
      { key: 'workflow-builder', title: 'Workflow Builder', icon: 'ri-git-branch-line', section: 'system' },
      { key: 'email-templates', title: 'Email Templates', icon: 'ri-mail-line', section: 'system' },
      { key: 'webhooks', title: 'Webhooks', icon: 'ri-link', section: 'system' },
      { key: 'ai-chatbot', title: 'AI Chatbot', icon: 'ri-robot-line', section: 'system' },
      { key: 'data-import-export', title: 'Import/Export', icon: 'ri-download-cloud-line', section: 'system' },
      { key: 'milestone-tracker', title: 'Milestone Tracker', icon: 'ri-flag-line', section: 'system' },
    ],
  },
];

// RBAC: Define which sections/pages each role can access
export type Permission = 'view' | 'create' | 'edit' | 'delete' | 'approve';

export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: ['*'],

  hr_manager: [
    'dashboard', 'employee-directory', 'employee-self-service', 'my-team',
    'departments', 'designations', 'locations',
    'shifts', 'shift-roster', 'attendance', 'attendance-summary',
    'wfh-settings', 'wfh-requests',
    'leave-types', 'leave-settings', 'leave-balance', 'leave-applications', 'leave-approvals', 'leave-team-calendar', 'leave-encashment', 'holiday-calendar',
    'payroll-dashboard', 'salary-components', 'salary-structures', 'payroll-run', 'payslips', 'loan-management',
    'job-postings', 'requisitions', 'candidate-pipeline', 'resume-parser', 'employee-referrals', 'interview-schedule', 'offer-management', 'career-fair', 'hiring-analytics', 'internal-mobility',
    'performance-cycles', 'goals', 'feedback', 'nine-box', 'calibration', 'competency-framework', 'pip-tracker', 'team-performance',
    'training', 'training-calendar', 'training-materials', 'training-certificates', 'training-feedback', 'learning-paths', 'mentoring-program',
    'expenses', 'travel-management', 'tickets',
    'projects', 'timesheets',
    'surveys', 'survey-analytics', 'pulse-survey',
    'assets', 'asset-categories', 'asset-requests', 'asset-vendors', 'asset-purchases', 'asset-maintenance', 'asset-depreciation', 'asset-licenses', 'asset-stock', 'asset-cover', 'asset-incidents', 'asset-labels', 'asset-controls',
    'onboarding', 'onboarding-guide', 'offboarding',
    'policies', 'benefits', 'rewards', 'documents', 'announcements', 'notifications', 'company-calendar', 'grievance-management', 'recognition-wall', 'meeting-scheduler', 'employee-engagement',
    'reports', 'workforce-planning', 'attrition-risk', 'org-chart', 'separation-analysis',
    'compliance-dashboard', 'contingent-workers', 'visa-immigration', 'workplace-safety', 'risk-register',
    'setup', 'settings', 'picklists', 'roles-permissions', 'audit-log',
  ],

  finance_lead: [
    'dashboard', 'employee-self-service', 'my-team',
    'payroll-dashboard', 'salary-components', 'salary-structures', 'payroll-run', 'payslips', 'loan-management',
    'expenses', 'travel-management',
    'reports',
  ],

  department_head: [
    'dashboard', 'employee-directory', 'employee-self-service', 'my-team',
    'departments', 'designations', 'locations',
    'shifts', 'shift-roster', 'shift-swaps', 'attendance', 'attendance-summary', 'regularization',
    'wfh-requests',
    'leave-balance', 'leave-applications', 'leave-approvals', 'leave-team-calendar', 'holiday-calendar',
    'job-postings', 'requisitions', 'candidate-pipeline', 'interview-schedule', 'offer-management', 'internal-mobility',
    'performance-cycles', 'goals', 'feedback', 'nine-box', 'calibration', 'team-performance',
    'training', 'training-calendar', 'learning-paths', 'mentoring-program',
    'expenses', 'travel-management', 'tickets',
    'projects', 'timesheets',
    'onboarding', 'onboarding-guide', 'offboarding',
    'documents', 'announcements', 'company-calendar', 'meeting-scheduler', 'employee-engagement',
    'reports', 'attrition-risk', 'org-chart', 'workforce-planning',
  ],

  manager: [
    'dashboard', 'employee-directory', 'employee-self-service', 'my-team',
    'attendance', 'regularization', 'wfh-requests',
    'leave-balance', 'leave-applications', 'leave-approvals', 'holiday-calendar',
    'performance-cycles', 'goals', 'feedback', 'team-performance',
    'expenses', 'travel-management', 'tickets',
    'projects', 'timesheets',
    'onboarding', 'onboarding-guide', 'offboarding',
    'announcements', 'company-calendar', 'meeting-scheduler',
    'reports', 'attrition-risk', 'org-chart',
  ],

  employee: [
    'dashboard', 'employee-self-service', 'my-team',
    'attendance', 'punch-clock', 'field-tracker', 'attendance-summary', 'regularization', 'wfh-requests',
    'leave-balance', 'leave-applications', 'leave-team-calendar', 'holiday-calendar',
    'goals', 'feedback',
    'training', 'training-calendar', 'training-materials', 'training-certificates', 'learning-paths',
    'expenses', 'travel-management', 'tickets',
    'projects', 'timesheets',
    'onboarding', 'onboarding-guide', 'offboarding',
    'policies', 'benefits', 'rewards', 'documents', 'announcements', 'notifications', 'company-calendar', 'recognition-wall',
    'payslips', 'loan-management',
    'help-center', 'quick-actions',
    'surveys', 'pulse-survey',
    'employee-engagement',
  ],

  recruiter: [
    'dashboard', 'employee-directory', 'employee-self-service', 'my-team',
    'job-postings', 'requisitions', 'candidate-pipeline', 'resume-parser', 'employee-referrals', 'interview-schedule', 'offer-management', 'career-fair', 'hiring-analytics', 'internal-mobility',
    'announcements', 'company-calendar',
    'reports',
    'onboarding', 'onboarding-guide',
    'help-center',
  ],

  auditor: [
    'dashboard', 'employee-directory',
    'departments', 'designations', 'locations',
    'attendance', 'attendance-summary',
    'leave-applications', 'leave-approvals',
    'payroll-dashboard', 'payroll-run', 'payslips',
    'expenses',
    'compliance-dashboard', 'contingent-workers', 'visa-immigration', 'workplace-safety', 'risk-register',
    'audit-log', 'reports',
    'policies', 'documents',
  ],
};

// Check if a role has access to a page
export function hasPageAccess(role: UserRole, pageKey: string): boolean {
  const perms = ROLE_PERMISSIONS[role];
  if (perms.includes('*')) return true;
  return perms.includes(pageKey);
}

// Get filtered navigation sections for a role
export function getNavSectionsForRole(role: UserRole): NavSection[] {
  return NAV_SECTIONS
    .map(section => ({
      ...section,
      pages: section.pages.filter(page => hasPageAccess(role, page.key)),
    }))
    .filter(section => section.pages.length > 0);
}

/**
 * Permission-driven navigation: filter sections/pages by the user's canonical HRM
 * permission codes (from the JWT). Admins see everything.
 *
 * When permissions have not loaded yet, show only public self-service pages —
 * never the full ROLE_PERMISSIONS dump (that previously leaked admin menus).
 */
export function getNavSectionsForPermissions(
  permissions: string[],
  isAdmin: boolean,
  _fallbackRole?: UserRole,
): NavSection[] {
  if (permissions.length === 0 && !isAdmin) {
    return NAV_SECTIONS
      .map((section) => ({
        ...section,
        pages: section.pages.filter((page) => canViewPage(page.key, { permissions: [], isAdmin: false })),
      }))
      .filter((section) => section.pages.length > 0);
  }
  return NAV_SECTIONS
    .map(section => ({
      ...section,
      pages: section.pages.filter(page => canViewPage(page.key, { permissions, isAdmin })),
    }))
    .filter(section => section.pages.length > 0);
}

// Get all page keys
export function getAllPageKeys(): string[] {
  return NAV_SECTIONS.flatMap(s => s.pages.map(p => p.key));
}

// Get page info by key
export function getPageInfo(key: string): NavPage | undefined {
  return NAV_SECTIONS.flatMap(s => s.pages).find(p => p.key === key);
}

const SECTION_LABELS: Record<string, string> = {
  main: 'Main',
  self: 'Self Service',
  people: 'People',
  org: 'Organization',
  time: 'Time & Attendance',
  leave: 'Leave',
  payroll: 'Payroll',
  recruit: 'Recruitment',
  perf: 'Performance',
  training: 'Learning',
  ops: 'Operations',
  assetmod: 'Assets',
  engage: 'Engagement',
  analytics: 'Insights',
  compliance: 'Compliance',
  system: 'Administration',
  // Legacy dashboard-config section keys
  manage: 'Operations',
  projects: 'Operations',
  surveys: 'Engagement',
  assets: 'Operations',
  lifecycle: 'People',
};

const SECTION_ORDER: Record<string, number> = Object.fromEntries(
  Object.keys(SECTION_LABELS).map((k, i) => [k, i]),
);

/**
 * Build sidebar sections from dashboard config menu items (role × level resolver).
 * Falls back to page metadata from NAV_SECTIONS when a pageKey is unknown.
 */
export function getNavSectionsFromMenuItems(menuItems: DashboardMenuItem[]): NavSection[] {
  const bySection = new Map<string, NavPage[]>();

  for (const item of menuItems) {
    const known = getPageInfo(item.pageKey);
    const page: NavPage = known ?? {
      key: item.pageKey,
      title: item.label,
      icon: item.icon,
      section: item.section,
    };
    if (!bySection.has(item.section)) bySection.set(item.section, []);
    const list = bySection.get(item.section)!;
    if (!list.some((p) => p.key === page.key)) {
      list.push(page);
    }
  }

  return Array.from(bySection.entries())
    .map(([key, pages]) => ({
      key,
      label: SECTION_LABELS[key] ?? key.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      order: SECTION_ORDER[key] ?? 99,
      pages,
    }))
    .sort((a, b) => a.order - b.order)
    .filter((section) => section.pages.length > 0);
}
